import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../utils/api';

const AdminPanel = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadAdminData();
  }, [isAuthenticated, user, navigate]);

  const loadAdminData = async () => {
    try {
      const [usersData, paymentsData] = await Promise.all([
        api.getAdminUsers(),
        api.getAdminPayments()
      ]);
      setUsers(usersData.users || []);
      setPayments(paymentsData.submissions || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = (userToUpgrade) => {
    setSelectedUser(userToUpgrade);
    setShowUpgradeModal(true);
  };

  const downloadUsersCsv = () => {
    const headers = ['Email', 'Username', 'Tier', 'Readings Today', 'Weekly Readings', 'Subscription End', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...users.map(u => [
        u.email,
        u.username,
        u.subscription_tier,
        u.readings_today || 0,
        u.weekly_readings_used || 0,
        u.subscription_end || 'N/A',
        u.created_at
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bitarot-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="container fade-in" data-testid="admin-panel">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        👑 Admin Panel
      </h1>

      {/* Tab Navigation */}
      <div style={{display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '2rem'}}>
        <button
          className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({users.length})
        </button>
        <button
          className={`btn ${activeTab === 'payments' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('payments')}
        >
          💳 Payment Submissions ({payments.length})
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
            <h2 style={{fontSize: '1.5rem', color: '#8b5cf6'}}>📋 Registered Users</h2>
            <button onClick={downloadUsersCsv} className="btn btn-secondary">
              📥 Download CSV
            </button>
          </div>
          
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{borderBottom: '2px solid rgba(139, 92, 246, 0.3)'}}>
                  <th style={tableHeaderStyle}>Email</th>
                  <th style={tableHeaderStyle}>Username</th>
                  <th style={tableHeaderStyle}>Tier</th>
                  <th style={tableHeaderStyle}>Readings</th>
                  <th style={tableHeaderStyle}>Subscription End</th>
                  <th style={tableHeaderStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{borderBottom: '1px solid rgba(139, 92, 246, 0.1)'}}>
                    <td style={tableCellStyle}>{u.email}</td>
                    <td style={tableCellStyle}>{u.username}</td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.85rem',
                        background: getTierBadgeColor(u.subscription_tier),
                        color: 'white'
                      }}>
                        {u.subscription_tier}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {u.subscription_tier === 'free' 
                        ? `${u.weekly_readings_used || 0}/3 weekly`
                        : `${u.readings_today || 0}/${u.daily_reading_limit || 0} daily`
                      }
                    </td>
                    <td style={tableCellStyle}>
                      {u.subscription_end 
                        ? new Date(u.subscription_end).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                        <button 
                          onClick={() => handleUpgradeClick(u)}
                          className="btn btn-primary"
                          style={{padding: '0.5rem 1rem', fontSize: '0.85rem'}}
                          data-testid={`upgrade-btn-${u.email}`}
                        >
                          {u.subscription_tier === 'top' ? '🔄 Renew' : '⬆️ Upgrade'}
                        </button>
                        <button 
                          onClick={() => { setResetPasswordUser(u); setShowResetPasswordModal(true); }}
                          className="btn btn-secondary"
                          style={{padding: '0.5rem 1rem', fontSize: '0.85rem'}}
                          data-testid={`reset-pwd-btn-${u.email}`}
                        >
                          🔑 Reset Password
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="card">
          <h2 style={{fontSize: '1.5rem', color: '#8b5cf6', marginBottom: '1.5rem'}}>💳 Payment Submissions</h2>
          
          {payments.length === 0 ? (
            <p style={{textAlign: 'center', color: '#9ca3af'}}>No payment submissions yet.</p>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {payments.map((payment, idx) => (
                <div key={idx} style={{
                  padding: '1.5rem',
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '0.75rem',
                  border: payment.status === 'pending' ? '2px solid #f59e0b' : '1px solid rgba(139, 92, 246, 0.2)'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem'}}>
                    <div>
                      <p style={{fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem'}}>
                        {payment.email}
                      </p>
                      <p style={{color: '#cbd5e1', fontSize: '0.9rem'}}>
                        Requested: <strong>{payment.subscription_tier}</strong>
                      </p>
                      <p style={{color: '#cbd5e1', fontSize: '0.9rem'}}>
                        Amount: <strong>{payment.amount_usdt} USDT</strong> ({payment.network})
                      </p>
                      <p style={{color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.5rem'}}>
                        From Wallet: {payment.wallet_address?.substring(0, 20)}...
                      </p>
                      {payment.transaction_hash && (
                        <p style={{color: '#9ca3af', fontSize: '0.85rem'}}>
                          TX: {payment.transaction_hash?.substring(0, 30)}...
                        </p>
                      )}
                      {payment.notes && (
                        <p style={{color: '#9ca3af', fontSize: '0.85rem', fontStyle: 'italic'}}>
                          Notes: {payment.notes}
                        </p>
                      )}
                    </div>
                    <div style={{textAlign: 'right'}}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.85rem',
                        background: payment.status === 'pending' ? '#f59e0b' : '#22c55e',
                        color: 'white'
                      }}>
                        {payment.status}
                      </span>
                      <p style={{color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.5rem'}}>
                        {new Date(payment.submitted_at).toLocaleString()}
                      </p>
                      {payment.status === 'pending' && (
                        <button 
                          onClick={() => {
                            const user = users.find(u => u.id === payment.user_id);
                            if (user) handleUpgradeClick(user);
                          }}
                          className="btn btn-primary"
                          style={{marginTop: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.85rem'}}
                        >
                          ✅ Verify & Activate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedUser && (
        <UpgradeModal
          user={selectedUser}
          onClose={() => {
            setShowUpgradeModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            loadAdminData();
            setShowUpgradeModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => {
            setShowResetPasswordModal(false);
            setResetPasswordUser(null);
          }}
          onSuccess={() => {
            setShowResetPasswordModal(false);
            setResetPasswordUser(null);
          }}
        />
      )}
    </div>
  );
};

// Upgrade Modal Component
const UpgradeModal = ({ user, onClose, onSuccess }) => {
  const [tier, setTier] = useState(user.subscription_tier === 'free' ? 'basic' : 'top');
  const [months, setMonths] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await api.activateSubscription({
        user_id: user.id,
        subscription_tier: tier,
        subscription_months: months
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const isRenew = user.subscription_tier === 'top';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-center glow-text" style={{fontSize: '2rem', marginBottom: '1rem'}}>
          {isRenew ? '🔄 Renew Subscription' : '⬆️ Upgrade User'}
        </h2>
        
        <div style={{marginBottom: '1.5rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem'}}>
          <p style={{color: '#8b5cf6', fontWeight: 'bold'}}>{user.email}</p>
          <p style={{color: '#cbd5e1', fontSize: '0.9rem'}}>Current Tier: {user.subscription_tier}</p>
          {user.subscription_end && (
            <p style={{color: new Date(user.subscription_end) < new Date() ? '#ef4444' : '#22c55e', fontSize: '0.9rem'}}>
              {new Date(user.subscription_end) < new Date() ? 'Expired' : 'Expires'}: {new Date(user.subscription_end).toLocaleDateString()}
            </p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Subscription Tier</label>
            <select className="form-select" value={tier} onChange={(e) => setTier(e.target.value)}>
              {user.subscription_tier !== 'basic' && <option value="basic">Basic ($6/mo)</option>}
              <option value="top">Top ($12/mo)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Duration (Months)</label>
            <select className="form-select" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              <option value={1}>1 Month</option>
              <option value={6}>6 Months</option>
              <option value={12}>12 Months (1 Year)</option>
            </select>
          </div>

          <div style={{display: 'flex', gap: '1rem'}}>
            <button type="button" onClick={onClose} className="btn btn-secondary" style={{flex: 1}}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{flex: 1}} disabled={submitting}>
              {submitting ? 'Activating...' : (isRenew ? '🔄 Renew Subscription' : '✅ Activate Subscription')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reset Password Modal Component
const ResetPasswordModal = ({ user, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await api.adminResetPassword(user.id, newPassword);
      setSuccess(result.message);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="reset-password-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-center glow-text" style={{fontSize: '2rem', marginBottom: '1rem'}}>
          🔑 Reset Password
        </h2>
        
        <div style={{marginBottom: '1.5rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem'}}>
          <p style={{color: '#8b5cf6', fontWeight: 'bold'}}>{user.email}</p>
          <p style={{color: '#cbd5e1', fontSize: '0.9rem'}}>Username: {user.username}</p>
        </div>

        {error && <div className="error-message" data-testid="reset-pwd-error">{error}</div>}
        {success && <div style={{padding: '0.75rem', background: 'rgba(34,197,94,0.2)', border: '1px solid #22c55e', borderRadius: '0.5rem', color: '#22c55e', marginBottom: '1rem', textAlign: 'center'}} data-testid="reset-pwd-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="text"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 chars)"
              required
              data-testid="reset-pwd-input"
            />
          </div>

          <div style={{display: 'flex', gap: '1rem'}}>
            <button type="button" onClick={onClose} className="btn btn-secondary" style={{flex: 1}} data-testid="reset-pwd-cancel">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{flex: 1}} disabled={submitting} data-testid="reset-pwd-submit">
              {submitting ? 'Resetting...' : '🔑 Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper styles
const tableHeaderStyle = {
  textAlign: 'left',
  padding: '1rem',
  color: '#8b5cf6',
  fontWeight: 'bold'
};

const tableCellStyle = {
  padding: '1rem',
  color: '#cbd5e1'
};

const getTierBadgeColor = (tier) => {
  switch (tier) {
    case 'free': return 'linear-gradient(135deg, #6b7280, #4b5563)';
    case 'basic': return 'linear-gradient(135deg, #8b5cf6, #ec4899)';
    case 'top': return 'linear-gradient(135deg, #f59e0b, #ef4444)';
    default: return '#6b7280';
  }
};

export default AdminPanel;
